import React, { useCallback, useState } from "react";
import { Gift } from "lucide-react";
import { api } from "../../lib/api";
import { AFFILIATE_PATHS } from "../../affiliate/contract";
import { useT } from "../../lib/LanguageContext";
import { formatMoneyCode } from "../../lib/formatMoney";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";
import defaultGatewayApi from "../../gateway/api";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import ProgramPolicyForm from "./ProgramPolicyForm";
import AffiliateRulesEditor from "./AffiliateRulesEditor";
import AffiliatesTable from "./AffiliatesTable";
import AffiliateDetail, { REFERRAL_TILE_KEYS, isReferralCounts, referralTiles } from "./AffiliateDetail";
import CommissionsTable from "./CommissionsTable";
import LegacyCommissionsPanel from "./LegacyCommissionsPanel";

const TABS = [
  { key: "overview", labelKey: "saff.tab.overview" },
  { key: "affiliates", labelKey: "saff.tab.affiliates" },
  { key: "commissions", labelKey: "saff.tab.commissions" },
  { key: "rules", labelKey: "saff.tab.rules" },
  { key: "policy", labelKey: "saff.tab.policy" },
  { key: "legacy", labelKey: "saff.tab.legacy" },
];

function Card({ label, value, testId }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testId}>
      <div className="text-2xl text-slate-800 tabular-nums">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
}

function OverviewTab() {
  const t = useT("superadmin");
  const tv = useT("common");
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(AFFILIATE_PATHS.adminOverview);
    if (!data || typeof data !== "object" || typeof data.affiliates !== "number"
        || !isReferralCounts(data.referrals) || !data.commissions) {
      throw payloadShapeError();
    }
    setOverview(data);
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  if (loadError) {
    return (
      <LoadFailure
        message={affiliateErrorText(loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="sa-affiliate-overview-load-error"
        retryTestId="sa-affiliate-overview-retry"
      />
    );
  }
  if (!overview) return <p className="text-slate-400 text-sm">{t("saff.loading")}</p>;

  const money = (cents) => formatMoneyCode(cents, overview.commissions.currency);
  const referrals = referralTiles(overview.referrals);

  return (
    <div className="space-y-4" data-testid="sa-affiliate-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label={t("saff.overview.affiliates")} value={overview.affiliates} testId="sa-overview-affiliates" />
        <Card label={t("saff.overview.referralsTotal")} value={referrals.total} testId="sa-overview-referrals-total" />
        {referrals.states.map((s) => (
          <Card key={s} label={t(REFERRAL_TILE_KEYS[s])} value={overview.referrals[s]} testId={`sa-overview-${s}`} />
        ))}
        <Card label={t("saff.overview.accrued")} value={money(overview.commissions.accruedCents)} testId="sa-overview-accrued" />
        <Card label={t("saff.overview.onHold")} value={money(overview.commissions.onHoldCents)} testId="sa-overview-onhold" />
        <Card label={t("saff.overview.reversed")} value={money(overview.commissions.reversedCents)} testId="sa-overview-reversed" />
        <Card label={t("saff.overview.clicks30d")} value={overview.clicks30d} testId="sa-overview-clicks" />
      </div>
      {/* Stated rather than assumed: what an affiliate may WITHDRAW is a wallet question and
          the wallet is where the operator reads it. Every figure above is derived at read
          time from the ledger-backed collections — nothing here is a stored counter. */}
      <p className="text-xs text-slate-400">{t("saff.overview.note")}</p>
    </div>
  );
}

/**
 * The platform operator's affiliate console.
 *
 * Six tabs, one per question an operator actually arrives with: how is the program doing,
 * who is in it, what has it cost, what are the rates, what is the policy, and what is left
 * of the v0 program. The four money-moving writes (policy, a rule, an account's status, a
 * reversal) are step-up gated through the gateway's ONE dialog and its ONE token mint;
 * there is no affiliate copy of either.
 *
 * `gatewayApi` is a prop for the same reason every gateway screen takes one: it is the seam
 * `StepUpAuth` mints the re-authentication token through.
 */
export default function AffiliateManager({ gatewayApi = defaultGatewayApi }) {
  const t = useT("superadmin");
  const [tab, setTab] = useState("overview");
  const [openAffiliate, setOpenAffiliate] = useState(null);

  return (
    <div data-testid="sa-affiliate" className="text-slate-800 space-y-6">
      <div>
        <h1 className="text-3xl flex items-center gap-3">
          <Gift className="w-6 h-6 text-sol-gold" /> {t("saff.title")}
        </h1>
        <p className="text-sm text-slate-400 mt-1">{t("saff.subtitle")}</p>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-slate-200" role="tablist">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            role="tab"
            aria-selected={tab === tb.key}
            onClick={() => { setTab(tb.key); setOpenAffiliate(null); }}
            data-testid={`sa-affiliate-tab-${tb.key}`}
            className={`px-4 py-2 text-sm rounded-t-lg ${
              tab === tb.key ? "bg-white border border-b-white border-slate-200 text-slate-900" : "text-slate-500"
            }`}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "affiliates" && (
        openAffiliate
          ? <AffiliateDetail tenantId={openAffiliate} onBack={() => setOpenAffiliate(null)} gatewayApi={gatewayApi} />
          : <AffiliatesTable onOpen={setOpenAffiliate} />
      )}
      {tab === "commissions" && <CommissionsTable gatewayApi={gatewayApi} />}
      {tab === "rules" && <AffiliateRulesEditor gatewayApi={gatewayApi} />}
      {tab === "policy" && <ProgramPolicyForm gatewayApi={gatewayApi} />}
      {tab === "legacy" && <LegacyCommissionsPanel />}
    </div>
  );
}
